import React, {Component} from 'react';
import styled from 'styled-components';
import ReCAPTCHA from 'react-google-recaptcha';
import Button from "../App/Button";
import Loader from "../Common/Loader";
import {sendComplaintMail} from "../../helpers";

const Wrapper = styled.div`
  max-height: 100%;
  overflow-y: auto;
  padding: 20px;
`;

const Title = styled.h2`
  margin: 0 0 20px 0;
  font-size: 24px;
  color: #333;
`;

const IntroText = styled.div`
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.5;
  color: #555;
`;

const ClosingText = styled.div`
  margin-top: 20px;
  font-size: 14px;
  line-height: 1.5;
  color: #555;
`;

const Row = styled.div`
  margin: 15px -15px;
  display: flex;
  align-items: center;
`;

const Col = styled.div`
  padding: 0 15px;
  ${({half}) => half ? 'width: 50%;' : ''}
  ${({half}) => !half ? 'flex: 1;' : ''}
  ${({align}) => align === 'right' ? 'text-align: right;' : ''}
  ${({align}) => align === 'center' ? 'text-align: center;' : ''}
`;

const Input = styled.input`
  display: block;
  background: #EDEDED;
  border: none;
  border-radius: 3px;
  width: 100%;
  padding: 10px 15px;
  font-size: 14px;
`;

const Textarea = styled.textarea`
  display: block;
  background: #EDEDED;
  border: none;
  border-radius: 3px;
  width: 100%;
  padding: 10px 10px;
  font-size: 14px;
`;

const Message = styled.div`
  background: #22b14c;
  padding: 15px;
  border-radius: 3px;
  color: #fff;
  margin: 30px 0;
`;

const ErrorMessage = styled.div`
  background: #c0392b;
  padding: 15px;
  border-radius: 3px;
  color: #fff;
  margin: 30px 0;
`;

const Checkbox = styled.div`
  display: flex;
  > div {
    flex: 1;
    margin-left: 10px;
  }
  a:hover {
    text-decoration: underline;
  }
`;

class ComplaintsForm extends Component {

  state = {
    form: {
      name: '',
      email: '',
      pageUrl: '',
      reason: '',
      privacyPolicy: false
    },
    recaptcha: null,
    sending: false,
    response: null
  }

  constructor(props) {
    super(props);
    this.props = props;

    //serve per azzerare il widget: i token reCAPTCHA v2 sono monouso,
    //riusarne uno già consumato viene rifiutato dal server
    this.recaptchaRef = React.createRef();

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleReCAPTCHA = this.handleReCAPTCHA.bind(this);
  }

  resetReCAPTCHA() {
    if (this.recaptchaRef.current) {
      this.recaptchaRef.current.reset();
    }
    this.setState({recaptcha: null});
  }

  componentDidMount() {
    // Pre-compila l'URL della pagina corrente
    this.setState({
      form: {
        ...this.state.form,
        pageUrl: window.location.href
      }
    });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.reset !== this.props.reset) {
      this.handleReset();
    }
  }

  handleChange(e) {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    this.setState({
      form: Object.assign({}, this.state.form, {
        [e.target.name]: value
      })
    });
  }

  async handleSubmit(e) {
    e.preventDefault();

    if (this.state.sending) return;

    await this.setState({
      sending: true,
      response: null
    });

    try {

      await sendComplaintMail({
        fromEmail: this.state.form.email,
        fromName: this.state.form.name,
        pageUrl: this.state.form.pageUrl,
        reason: this.state.form.reason,
        reCaptcha: this.state.recaptcha,
        lang: this.props.lang,
        config: this.props.config
      });

      //solo in caso di invio riuscito svuoto il form
      this.setState({
        sending: false,
        response: 'success',
        form: {
          privacyPolicy: false,
          name: '',
          email: '',
          pageUrl: window.location.href,
          reason: ''
        }
      });

    } catch (error) {

      console.error('invio segnalazione fallito', error);

      //il form resta compilato così l'utente può ritentare senza riscrivere tutto
      this.setState({
        sending: false,
        response: 'error'
      });

    }

    //in ogni caso il token è stato consumato: va richiesta una nuova verifica
    this.resetReCAPTCHA();
  }

  handleReCAPTCHA(response) {
    this.setState({
      recaptcha: response
    });
  }

  handleReset() {
    this.setState({
      form: {
        privacyPolicy: false,
        name: '',
        email: '',
        pageUrl: window.location.href,
        reason: ''
      },
      sending: false,
      response: null
    });
    this.resetReCAPTCHA();
  }

  render() {
    const { translations, lang } = this.props;
    const { recaptcha, sending, response } = this.state;
    const { privacyPolicy } = this.state.form;

    return (
      <Wrapper>
        <Title>{translations["UI"]["complaints_form_title"] || translations["UI"]["website_complaints"]}</Title>
        
        <IntroText dangerouslySetInnerHTML={{__html: translations["UI"]["complaints_intro_text"] || ""}} />

        {response === 'success' && (
          <Message>
            <div>{translations["UI"]["complaints_message_sent"] || translations["UI"]["_contactform-messagesent"]}</div>
          </Message>
        )}

        {response === 'error' && (
          <ErrorMessage>
            <div>{translations["UI"]["complaints_message_error"] || "Non è stato possibile inviare la segnalazione. Verifica di aver completato la verifica di sicurezza e riprova."}</div>
          </ErrorMessage>
        )}

        <form onSubmit={this.handleSubmit}>
          <Row>
            <Col half>
              <label htmlFor="name">{translations["UI"]["_contactform-name"]}</label>
              <Input 
                placeholder={translations["UI"]["_contactform-name"]} 
                name='name' 
                value={this.state.form.name} 
                type='text'
                onChange={this.handleChange} 
                required
              />
            </Col>
            <Col half>
              <label htmlFor="email">{translations["UI"]["_contactform-email"]}</label>
              <Input 
                placeholder={translations["UI"]["_contactform-email"]} 
                name='email' 
                value={this.state.form.email} 
                type='email'
                onChange={this.handleChange} 
                required
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <label htmlFor="pageUrl">{translations["UI"]["complaints_page_url"] || "URL della pagina"}</label>
              <Input 
                placeholder={translations["UI"]["complaints_page_url"] || "URL della pagina"} 
                name='pageUrl' 
                value={this.state.form.pageUrl} 
                type='url'
                onChange={this.handleChange} 
                required
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <label htmlFor="reason">{translations["UI"]["complaints_reason"] || "Motivazione"}</label>
              <Textarea 
                rows={6} 
                placeholder={translations["UI"]["complaints_reason"] || "Motivazione"} 
                name='reason' 
                onChange={this.handleChange}
                value={this.state.form.reason} 
                required
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <ReCAPTCHA
                ref={this.recaptchaRef}
                sitekey="6LfATGIUAAAAABdE2N1SJwR1xqkHj5uq9I6yX8Jm"
                onChange={this.handleReCAPTCHA}
                onExpired={() => this.setState({recaptcha: null})}
                onErrored={() => this.setState({recaptcha: null})}
                hl={this.props.lang}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Checkbox>
                <input 
                  onChange={this.handleChange} 
                  checked={this.state.form.privacyPolicy}
                  required 
                  name="privacyPolicy" 
                  type="checkbox" 
                />
                <div>
                  <a 
                    rel="noopener noreferrer" 
                    target="_blank" 
                    href={translations["UI"]["complaints_privacy_url"] || `/${lang === 'it' ? 'it' : 'en'}/contact-policy`}
                  >
                    {translations['UI']['read_privacy_policy']}
                  </a>
                </div>
              </Checkbox>
            </Col>
          </Row>

          <ClosingText dangerouslySetInnerHTML={{__html: translations["UI"]["complaints_closing_text"] || ""}} />

          <Row>
            <Col align={'center'}>
              {sending ? (
                <Loader size={{value: 20, unit: 'px'}}/>
              ) : (
                <Button 
                  fullSize={true} 
                  disabled={!privacyPolicy || !recaptcha} 
                  type='submit'
                >
                  {translations["UI"]["Send message"]}
                </Button>
              )}
            </Col>
          </Row>
        </form>
      </Wrapper>
    );
  }
}

export default ComplaintsForm;
